import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import Cookies from 'js-cookie';
import Box from '@mui/material/Box';
import MicNoneIcon from '@mui/icons-material/MicNone';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PauseOutlinedIcon from '@mui/icons-material/PauseOutlined';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RestartAltOutlinedIcon from '@mui/icons-material/RestartAltOutlined';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';

// Correct import for microphone-based VAD
import { MicVAD, utils } from '@ricky0123/vad-web';

import {
    setDescription as setReduxDescription,
    setTitle as setReduxTitle,
    setUpdatedTitle,
    setIsGenerateNewDescription,
    setIsLoadingTranscription as setReduxIsLoadingTranscription,
    setIsRecording as setReduxIsRecording,
    setSegments as setReduxSegments,
    appendSegment as appendReduxSegment,
    setIsLoadingAdditionalTranscription,
    setIsAdditionalRecording,
    setAdditionalSegments,
    appendAdditionalSegment,
    setIsRecorderBarOpened,
    setIsRecorderBarDisabled,
    setAudioTranscription,
    setAdditionalTranscription as setReduxAdditionalTranscription,
    setShouldAutoGenerateNote,
} from '../../../../../redux/features/doctor/Dashboard/consultation/consultationNote';
import { setIsConsentPopupOpened, setIsSettingsPopupOpened } from '../../../../../redux/features/doctor/Dashboard/consultation/consultation';
import { updateDoctorPreferences } from '../../../../../routes/doctor/patients';
import { setNotificationMessage } from '../../../../../redux/features/general/notification';
import Button from '../../../../general/inputs/Button';
import CancelButton from '../../../../general/inputs/CancelButton';

interface TranscriptionSegment {
    id: number | string;
    start: number; // Relative to the start of the audio chunk
    end: number;   // Relative to the start of the audio chunk (duration)
    text: string;
    isDelta?: boolean; // Will always be false with Gemini non-streaming
};

enum WebSocketState {
    CONNECTING = 0,
    OPEN = 1,
    CLOSING = 2,
    CLOSED = 3,
}

enum AudioState {
    STOPPED = 'stopped',
    RECORDING = 'recording', // Listening for speech via VAD
    PAUSED = 'paused',
    PROCESSING = 'processing', // VAD detected speech end, sending audio, waiting for transcription
    ERROR = 'error',
    INITIALIZING = 'initializing', // Initializing VAD or WebSocket
}

const TARGET_SAMPLE_RATE = 16000; // MicVAD defaults to 16000, but good to be explicit
const MAX_RECONNECT_ATTEMPTS = 5;
const VAD_SPEECH_END_TIMEOUT_MS = 1200; // Timeout for UI feedback if needed after speech ends

interface MicrophoneProps {
    isMobile: boolean;
    purpose: 'main' | 'additional';
}

// Helper to convert Float32Array (from VAD) to Int16 PCM ArrayBuffer
function float32ToInt16Pcm(float32Array: Float32Array): ArrayBuffer {
    const buffer = new ArrayBuffer(float32Array.length * 2);
    const view = new DataView(buffer);
    for (let i = 0; i < float32Array.length; i++) {
        const s = Math.max(-1, Math.min(1, float32Array[i]));
        view.setInt16(i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
    }
    return buffer;
}

// Helper to encode Int16 PCM ArrayBuffer to WAV ArrayBuffer
function pcmToWav(pcmData: ArrayBuffer, sampleRate: number): ArrayBuffer {
    const numChannels = 1;
    const bytesPerSample = 2; // For 16-bit PCM
    const blockAlign = numChannels * bytesPerSample;
    const byteRate = sampleRate * blockAlign;
    const dataSize = pcmData.byteLength;

    const buffer = new ArrayBuffer(44 + dataSize);
    const view = new DataView(buffer);

    const writeString = (viewInstance: DataView, offset: number, str: string) => {
        for (let i = 0; i < str.length; i++) {
            viewInstance.setUint8(offset + i, str.charCodeAt(i));
        }
    };

    writeString(view, 0, 'RIFF');
    view.setUint32(4, 36 + dataSize, true);
    writeString(view, 8, 'WAVE');
    writeString(view, 12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, byteRate, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bytesPerSample * 8, true);
    writeString(view, 36, 'data');
    view.setUint32(40, dataSize, true);
    new Uint8Array(buffer, 44).set(new Uint8Array(pcmData));

    return buffer;
}

// Helper to convert ArrayBuffer to Base64 string
function arrayBufferToBase64(buffer: ArrayBuffer): string {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return window.btoa(binary);
}


const Microphone: React.FC<MicrophoneProps> = ({ isMobile, purpose }) => {
    const dispatch = useAppDispatch();
    const { t } = useTranslation();
    const { consultationId, consultationNoteId } = useParams();

    const colors = useAppSelector(state => state.theme.colors);
    const securityPin = useAppSelector(state => state.consultation.securityPin);
    const isConsent = useAppSelector(state => state.consultation.isConsent);
    const globalIsMicrophoneDisabled = useAppSelector(state => state.consultationNote.isMicrophoneDisabled);

    useAppSelector(
        useCallback(
            (state: any) => purpose === 'main' ? state.consultationNote.segments : state.consultationNote.additionalSegments,
            [purpose]
        )
    );

    const isRecorderBarOpened = useAppSelector(state => state.consultationNote.isRecorderBarOpened);
    const isRecorderBarDisabled = useAppSelector(state => state.consultationNote.isRecorderBarDisabled);

    const [audioState, setAudioState] = useState<AudioState>(AudioState.STOPPED);
    const [recordingTime, setRecordingTime] = useState<number>(0);
    const [error, setError] = useState<string | null>(null);
    const [wsState, setWsState] = useState<WebSocketState>(WebSocketState.CLOSED);
    const [isMicActivatedBeforeConsent, setIsMicActivatedBeforeConsent] = useState(false);
    const [previousConsultationNoteId, setPreviousConsultationNoteId] = useState('');

    const timerIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const wsRef = useRef<WebSocket | null>(null);
    const reconnectAttemptRef = useRef<number>(0);
    const audioStateRef = useRef<AudioState>(audioState);

    const vadRef = useRef<MicVAD | null>(null); // For MicVAD instance
    const vadLastSpeechEndedTimerRef = useRef<NodeJS.Timeout | null>(null);
    const handleStartRecordingRequestRef = useRef<(() => void) | null>(null); // Ref for mutual recursion

    useEffect(() => {
        audioStateRef.current = audioState;
    }, [audioState]);

    const formatTime = (seconds: number): string => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const dispatchSetIsLoadingTranscription = useCallback((isLoading: boolean) => {
        if (purpose === 'main') {
            dispatch(setReduxIsLoadingTranscription(isLoading));
        } else {
            dispatch(setIsLoadingAdditionalTranscription(isLoading));
        }
    }, [dispatch, purpose]);

    const dispatchSetCurrentIsRecording = useCallback((isRec: boolean) => {
        if (purpose === 'main') {
            dispatch(setReduxIsRecording(isRec));
        } else {
            dispatch(setIsAdditionalRecording(isRec));
        }
    }, [dispatch, purpose]);

    const dispatchSetCurrentSegments = useCallback((segments: TranscriptionSegment[]) => {
        if (purpose === 'main') {
            dispatch(setReduxSegments(segments));
        } else {
            dispatch(setAdditionalSegments(segments));
        }
    }, [dispatch, purpose]);
    
    const dispatchAppendCurrentSegment = useCallback((segment: TranscriptionSegment) => {
        if (purpose === 'main') {
            dispatch(appendReduxSegment(segment));
        } else {
            dispatch(appendAdditionalSegment(segment));
        }
    }, [dispatch, purpose]);

    const sendAudioForTranscription = useCallback(async (audioPcm16Buffer: ArrayBuffer, durationSeconds: number) => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
            setAudioState(AudioState.PROCESSING);
            dispatchSetIsLoadingTranscription(true);

            const wavBuffer = pcmToWav(audioPcm16Buffer, TARGET_SAMPLE_RATE);
            const base64Wav = arrayBufferToBase64(wavBuffer);

            console.log(`WS Send (${purpose}): Sending audio_transcription_request. Duration: ${durationSeconds}s. WAV size: ${wavBuffer.byteLength} bytes.`);
            wsRef.current.send(JSON.stringify({
                type: 'audio_transcription_request',
                payload: {
                    audio_base64: base64Wav,
                    mime_type: 'audio/wav',
                    duration_seconds: durationSeconds,
                    language: navigator.language.split('-')[0] || 'en'
                }
            }));
        } else {
            console.warn(`WS Send (${purpose}): WebSocket not open. Cannot send audio.`);
            setError(t('connectionError'));
            setAudioState(AudioState.ERROR);
            dispatchSetIsLoadingTranscription(false);
        }
    }, [purpose, dispatchSetIsLoadingTranscription, t]);

    const initializeVAD = useCallback(async () => {
        if (vadRef.current) {
            vadRef.current.destroy();
            vadRef.current = null;
        }
        try {
            console.log(`VAD (${purpose}): Initializing MicVAD...`);
            const myVad = await MicVAD.new({
                minSpeechFrames: 5,
                positiveSpeechThreshold: 0.6,
                negativeSpeechThreshold: 0.4,
                preSpeechPadFrames: 3,
                redemptionFrames: 8,
                onSpeechStart: () => {
                    console.log(`VAD (${purpose}): Speech started.`);
                    if (vadLastSpeechEndedTimerRef.current) {
                        clearTimeout(vadLastSpeechEndedTimerRef.current);
                        vadLastSpeechEndedTimerRef.current = null;
                    }
                },
                onSpeechEnd: (audioFloat32: Float32Array) => {
                    console.log(`VAD (${purpose}): Speech ended. Audio length: ${audioFloat32.length} samples.`);
                    if (audioFloat32.length === 0) {
                        console.log(`VAD (${purpose}): Empty audio segment from onSpeechEnd, ignoring.`);
                        return;
                    }
                    
                    const pcmInt16Buffer = float32ToInt16Pcm(audioFloat32);
                    const durationSeconds = audioFloat32.length / TARGET_SAMPLE_RATE;
                    
                    sendAudioForTranscription(pcmInt16Buffer, durationSeconds);

                    if (vadLastSpeechEndedTimerRef.current) clearTimeout(vadLastSpeechEndedTimerRef.current);
                    vadLastSpeechEndedTimerRef.current = setTimeout(() => {
                        if (audioStateRef.current === AudioState.RECORDING) {
                             console.log(`VAD (${purpose}): Silence timeout, ready for new speech.`);
                        }
                    }, VAD_SPEECH_END_TIMEOUT_MS);
                },
            });
            vadRef.current = myVad;
            console.log(`VAD (${purpose}): MicVAD instance created successfully.`);
            return myVad;
        } catch (err: any) {
            console.error(`VAD (${purpose}): MicVAD initialization failed:`, err);
            let userMessage = t('vadError');
            if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                userMessage = t('micPermissionDenied');
            } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                userMessage = t('noMicFound');
            } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
                 userMessage = t('micInUse');
            } else {
                userMessage = `${t('micError')}: ${err.message || err.name || 'Unknown VAD init error'}`;
            }
            setError(userMessage);
            setAudioState(AudioState.ERROR);
            return null;
        }
    }, [purpose, sendAudioForTranscription, t, VAD_SPEECH_END_TIMEOUT_MS]); // audioStateRef is a ref, not needed in deps

    const disconnectWebSocket = useCallback((gracefulReason = "Client stopping") => {
        if (wsRef.current) {
            console.log(`WS Disconnect (${purpose}): Closing connection (${gracefulReason}).`);
            wsRef.current.onopen = null;
            wsRef.current.onmessage = null;
            wsRef.current.onerror = null;
            wsRef.current.onclose = null;
            if (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING) {
                 wsRef.current.close(1000, gracefulReason);
            }
            wsRef.current = null;
            setWsState(WebSocketState.CLOSED);
        }
    }, [purpose]);

    const stopAudioCapture = useCallback(async (gracefulShutdown: boolean = true) => {
        console.log(`Audio Stop (${purpose}): Stopping capture... (Graceful: ${gracefulShutdown})`);

        if (vadRef.current) {
            vadRef.current.destroy();
            vadRef.current = null;
            console.log(`VAD (${purpose}): MicVAD destroyed.`);
        }
        if (vadLastSpeechEndedTimerRef.current) {
            clearTimeout(vadLastSpeechEndedTimerRef.current);
            vadLastSpeechEndedTimerRef.current = null;
        }

        if (audioStateRef.current !== AudioState.STOPPED) {
             setAudioState(AudioState.STOPPED);
             dispatchSetCurrentIsRecording(false);
        }
        dispatchSetIsLoadingTranscription(false);

        if (gracefulShutdown && wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
            disconnectWebSocket(`Client stopped recording (${purpose})`);
        } else if (!gracefulShutdown) {
            wsRef.current = null;
            setWsState(WebSocketState.CLOSED);
        }
        console.log(`Audio Stop (${purpose}): Capture stopped.`);
    }, [dispatchSetCurrentIsRecording, dispatchSetIsLoadingTranscription, disconnectWebSocket, purpose]); // audioStateRef is a ref

    const startAudioCaptureInternal = useCallback(async () => {
        if (vadRef.current && (audioStateRef.current === AudioState.RECORDING || audioStateRef.current === AudioState.PAUSED || audioStateRef.current === AudioState.PROCESSING)) {
            console.log(`Audio Internal Start (${purpose}): MicVAD already exists and state is active. Ensuring listening.`);
            try {
                vadRef.current.start();
                setAudioState(AudioState.RECORDING);
                dispatchSetCurrentIsRecording(true);
                if (purpose === 'main') dispatch(setIsRecorderBarOpened(true));
                return;
            } catch (e) {
                console.error(`Audio Internal Start (${purpose}): Error ensuring MicVAD is listening:`, e);
                await stopAudioCapture(false);
            }
        }
        setAudioState(AudioState.INITIALIZING);
        setError(null);

        let activeVad = vadRef.current;
        if (!activeVad) {
            activeVad = await initializeVAD();
        }

        if (!activeVad) {
            console.error(`Audio Internal Start (${purpose}): VAD initialization failed or not available.`);
            dispatchSetCurrentIsRecording(false);
            dispatchSetIsLoadingTranscription(false);
            // setAudioState(AudioState.ERROR); // initializeVAD already sets ERROR state
            return;
        }

        try {
            activeVad.start();
            setAudioState(AudioState.RECORDING);
            dispatchSetCurrentIsRecording(true);
            if (purpose === 'main') dispatch(setIsRecorderBarOpened(true));
            console.log(`Audio Internal Start (${purpose}): MicVAD started. Listening for speech.`);
        } catch (err: any) {
            console.error(`Audio Internal Start (${purpose}): Error starting MicVAD:`, err);
            let userMessage = t('couldNotStartRecording');
             if (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError') {
                userMessage = t('micPermissionDenied');
            } else if (err.name === 'NotFoundError' || err.name === 'DevicesNotFoundError') {
                userMessage = t('noMicFound');
            } else if (err.name === 'NotReadableError' || err.name === 'TrackStartError') {
                 userMessage = t('micInUse');
            } else {
                userMessage = `${t('micError')}: ${err.message || err.name || 'Unknown VAD start error'}`;
            }
            setError(userMessage);
            setAudioState(AudioState.ERROR);
            dispatchSetCurrentIsRecording(false);
            dispatchSetIsLoadingTranscription(false);
            await stopAudioCapture(false);
            disconnectWebSocket(`Audio setup failed (${purpose})`);
        }
    }, [purpose, initializeVAD, stopAudioCapture, dispatchSetCurrentIsRecording, dispatchSetIsLoadingTranscription, disconnectWebSocket, t, dispatch]); // audioStateRef, vadRef are refs

    const connectWebSocket = useCallback(() => {
        if (!consultationId || !consultationNoteId || !securityPin) {
            console.error(`WS Connect (${purpose}): Missing IDs or PIN.`);
            setError(t('cannotStartRecordingMissingInfo'));
            dispatchSetIsLoadingTranscription(false);
            return false;
        }
        if (wsRef.current && (wsRef.current.readyState === WebSocket.OPEN || wsRef.current.readyState === WebSocket.CONNECTING)) {
            console.log(`WS Connect (${purpose}): Already open or connecting.`);
            if (audioStateRef.current === AudioState.STOPPED || audioStateRef.current === AudioState.ERROR) {
                startAudioCaptureInternal();
            }
            return true;
        }
        const token = Cookies.get('jwt');
        if (!token) {
            console.error(`WS Connect (${purpose}): Missing auth token.`);
            setError(t('authErrorRelogin'));
            dispatchSetIsLoadingTranscription(false);
            return false;
        }
        const backendWsHost = process.env.REACT_APP_BACKEND_WS_URL;
        if (!backendWsHost) {
            console.error("FATAL: REACT_APP_BACKEND_WS_URL is not defined.");
            setError(t('appConfigError'));
            dispatchSetIsLoadingTranscription(false);
            return false;
        }
        const wsProtocol = window.location.protocol === 'https:' ? 'wss://' : 'ws://';
        const wsUrl = `${wsProtocol}${backendWsHost}/ws/transcribe?token=${encodeURIComponent(token)}&consultationId=${consultationId}&consultationNoteId=${consultationNoteId}&securityPin=${securityPin}&streamType=${purpose}`;

        console.log(`WS Connect (${purpose}): Attempting connection to`, wsUrl);
        setWsState(WebSocketState.CONNECTING);

        try {
            const ws = new WebSocket(wsUrl);
            wsRef.current = ws;

            ws.onopen = () => {
                console.log(`WS Connect (${purpose}): WebSocket Connected`);
                setWsState(WebSocketState.OPEN);
                reconnectAttemptRef.current = 0;
                startAudioCaptureInternal();
            };

            ws.onmessage = (event) => {
                try {
                    if (typeof event.data === 'string') {
                        const message = JSON.parse(event.data);
                        console.log(`WS Message (${purpose}): Received`, message.type);
                        if (message.type === 'full_transcription_result' && message.payload) {
                            const segment = message.payload as TranscriptionSegment;
                            dispatchAppendCurrentSegment(segment);
                            dispatchSetIsLoadingTranscription(false);
                            if (audioStateRef.current === AudioState.PROCESSING) {
                                setAudioState(AudioState.RECORDING);
                            }
                            dispatch(setShouldAutoGenerateNote(true));
                        } else if (message.type === 'error') {
                            console.error(`WS Server Error (${purpose}):`, message.payload);
                            setError(`${t('transcriptionError')}: ${message.payload}`);
                            dispatchSetIsLoadingTranscription(false);
                            if (audioStateRef.current === AudioState.PROCESSING) {
                                setAudioState(AudioState.RECORDING);
                            }
                        } else if (message.type === 'info') {
                            console.log(`WS Info (${purpose}):`, message.payload);
                        }
                    } else {
                        console.warn(`WS Message (${purpose}): Received unexpected binary data:`, event.data);
                    }
                } catch (e) { console.error(`WS Message (${purpose}): Failed to parse:`, e); }
            };

            ws.onerror = (ev) => {
                console.error(`WS Error (${purpose}):`, ev);
                setError(t('connectionErrorTranscription'));
                setWsState(WebSocketState.CLOSED);
                dispatchSetIsLoadingTranscription(false);
            };

            ws.onclose = (ev) => {
                console.log(`WS Closed (${purpose}): Code=${ev.code}, Reason=${ev.reason}, Clean=${ev.wasClean}`);
                setWsState(WebSocketState.CLOSED);
                dispatchSetIsLoadingTranscription(false);

                if (audioStateRef.current !== AudioState.STOPPED && !ev.wasClean) {
                    setError(t('transcriptionConnectionLost'));
                    if (purpose === 'main') dispatch(setIsRecorderBarOpened(false));
                }
            };
            return true;
        } catch (error) {
            console.error(`WS Connect (${purpose}): Instantiation error:`, error);
            setError(t('failedToEstablishConnection'));
            setWsState(WebSocketState.CLOSED);
            dispatchSetIsLoadingTranscription(false);
            return false;
        }
    }, [consultationId, consultationNoteId, securityPin, purpose, dispatchSetIsLoadingTranscription, startAudioCaptureInternal, dispatchAppendCurrentSegment, t, dispatch]); // audioStateRef is a ref

    // Define resumeRecording before handleStartRecordingRequest
    const resumeRecording = useCallback(async () => {
        if (audioStateRef.current === AudioState.PAUSED && vadRef.current) {
            try {
                vadRef.current.start(); // MicVAD's start method also resumes if paused
                setAudioState(AudioState.RECORDING);
                console.log(`Resume (${purpose}): MicVAD resumed.`);
            } catch (e) {
                console.error(`Resume (${purpose}): Error resuming MicVAD:`, e);
                setError(t('errorResuming'));
            }
        } else if (wsState === WebSocketState.OPEN && (audioStateRef.current === AudioState.STOPPED || audioStateRef.current === AudioState.ERROR)) {
             console.log(`Resume (${purpose}): Attempting restart after unexpected stop/error.`);
             if (handleStartRecordingRequestRef.current) {
                handleStartRecordingRequestRef.current(); // Call via ref
             }
        } else {
            console.warn(`Resume (${purpose}): Cannot resume. State: ${audioStateRef.current}, VAD: ${!!vadRef.current}, WS: ${wsState}`);
        }
    }, [wsState, purpose, t]); // audioStateRef, vadRef are refs. handleStartRecordingRequestRef is a ref.

    const handleStartRecordingRequest = useCallback(() => {
        if (!consultationId || !consultationNoteId || !securityPin) {
            console.warn(`Start Request (${purpose}): Cannot start, missing IDs or PIN.`);
            setError(t('cannotStartRecordingMissingInfo'));
            return;
        }
        if ((audioStateRef.current !== AudioState.STOPPED && audioStateRef.current !== AudioState.ERROR) || wsState === WebSocketState.CONNECTING) {
            console.warn(`Start Request (${purpose}): Already active or connecting. State: ${audioStateRef.current}, WS: ${wsState}`);
            if (audioStateRef.current === AudioState.PAUSED) {
                resumeRecording(); // Direct call is fine, resumeRecording is defined above
            }
            return;
        }
        if (!isConsent || globalIsMicrophoneDisabled) {
            setIsMicActivatedBeforeConsent(true);
            dispatch(setIsConsentPopupOpened(true));
            return;
        }
        setError(null);
        dispatchSetCurrentSegments([]); 

        if (previousConsultationNoteId && String(consultationNoteId) !== String(previousConsultationNoteId)) {
            console.log(`Start Request (${purpose}): New consultation note detected.`);
        }
        setPreviousConsultationNoteId(String(consultationNoteId));

        const connectionInitiated = connectWebSocket();
        if (!connectionInitiated) {
             console.error(`Start Request (${purpose}): WebSocket connection failed to initiate immediately.`);
             setAudioState(AudioState.ERROR);
        } else if (connectionInitiated) {
            setAudioState(AudioState.INITIALIZING);
        }
    }, [
        wsState, isConsent, globalIsMicrophoneDisabled, consultationId, consultationNoteId, securityPin,
        previousConsultationNoteId, connectWebSocket, dispatchSetCurrentSegments, purpose, t, dispatch,
        resumeRecording // resumeRecording is now a valid dependency
    ]); // audioStateRef is a ref

    // Effect to update the ref for handleStartRecordingRequest
    useEffect(() => {
        handleStartRecordingRequestRef.current = handleStartRecordingRequest;
    }, [handleStartRecordingRequest]);


    const handleStopRecording = useCallback(async () => {
        console.log(`Stop (${purpose}): User initiated stop.`);
        if (audioStateRef.current === AudioState.STOPPED) {
            console.log(`Stop (${purpose}): Already stopped.`);
            return;
        }
        
        await stopAudioCapture(true);

        if (purpose === 'main') dispatch(setIsRecorderBarOpened(false));
        setRecordingTime(0);

    }, [stopAudioCapture, purpose, dispatch]); // audioStateRef is a ref

    const pauseRecording = useCallback(async () => {
        if (audioStateRef.current === AudioState.RECORDING && vadRef.current) {
            try {
                vadRef.current.pause();
                setAudioState(AudioState.PAUSED);
                console.log(`Pause (${purpose}): MicVAD paused.`);
            } catch (e) {
                console.error(`Pause (${purpose}): Error pausing MicVAD:`, e);
                setError(t('errorPausing'));
            }
        } else {
            console.warn(`Pause (${purpose}): Cannot pause. State: ${audioStateRef.current}, VAD: ${!!vadRef.current}`);
        }
    }, [purpose, t]); // audioStateRef, vadRef are refs

    const resetAudio = async () => {
        console.log(`Reset (${purpose}): Resetting audio.`);
        await stopAudioCapture(true);
        setRecordingTime(0);
        setError(null);
        dispatchSetCurrentSegments([]);
        dispatch(setNotificationMessage(t('audioRecordingReset')));
        setTimeout(() => {
            console.log(`Reset (${purpose}): Restarting audio...`);
            if (handleStartRecordingRequestRef.current) { // Use ref here as well for safety, or ensure handleStartRecordingRequest is stable if called directly
                handleStartRecordingRequestRef.current();
            }
        }, 300);
    };

    const handleSettings = () => { if (purpose === 'main') dispatch(setIsSettingsPopupOpened(true)); };
    const hideRecorderBar = async () => {
        if (purpose === 'main') {
            dispatch(setIsRecorderBarDisabled(true));
            dispatch(setIsRecorderBarOpened(false));
            if (audioStateRef.current !== AudioState.STOPPED) {
                await handleStopRecording();
            }
            updateDoctorPreferences({ consultation: { isRecorderBarDisabled: true } });
        }
    };

    useEffect(() => {
        if (audioState === AudioState.RECORDING || audioState === AudioState.PROCESSING) {
            if (!timerIntervalRef.current) {
                timerIntervalRef.current = setInterval(() => {
                    setRecordingTime(prevTime => prevTime + 1);
                }, 1000);
            }
        } else {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }
        }
        return () => {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }
        };
    }, [audioState]);


    useEffect(() => {
        if (isConsent && isMicActivatedBeforeConsent && consultationId && consultationNoteId && securityPin) {
            console.log(`Effect (${purpose}): Consent given, requesting start.`);
            if ((audioStateRef.current === AudioState.STOPPED || audioStateRef.current === AudioState.ERROR) && wsState !== WebSocketState.CONNECTING) {
                 if (handleStartRecordingRequestRef.current) {
                    handleStartRecordingRequestRef.current();
                 }
            }
            setIsMicActivatedBeforeConsent(false);
        }
    }, [isConsent, isMicActivatedBeforeConsent, consultationId, consultationNoteId, securityPin, wsState, purpose]); // audioStateRef is a ref. handleStartRecordingRequest is covered by the ref.

    useEffect(() => {
        if (globalIsMicrophoneDisabled && audioStateRef.current !== AudioState.STOPPED) {
            console.log(`Effect (${purpose}): Microphone disabled externally, stopping.`);
            handleStopRecording();
        }
    }, [globalIsMicrophoneDisabled, handleStopRecording, purpose]); // audioStateRef is a ref

    useEffect(() => {
        if (consultationNoteId && previousConsultationNoteId && consultationNoteId !== previousConsultationNoteId) {
            console.log(`Effect (${purpose}): Consultation Note ID changed, resetting state for this mic.`);
            if (audioStateRef.current !== AudioState.STOPPED) {
                stopAudioCapture(true);
            } else if (wsRef.current) {
                disconnectWebSocket(`Note ID changed (${purpose})`);
            }
            dispatchSetCurrentSegments([]);
            if (purpose === 'main') {
                dispatch(setReduxDescription(''));
                dispatch(setAudioTranscription(''));
            } else {
                dispatch(setReduxAdditionalTranscription(''));
            }
            setError(null);
            setRecordingTime(0);
            setPreviousConsultationNoteId(consultationNoteId);
        } else if (!previousConsultationNoteId && consultationNoteId) {
            setPreviousConsultationNoteId(consultationNoteId);
        }
    }, [consultationNoteId, previousConsultationNoteId, dispatch, stopAudioCapture, disconnectWebSocket, purpose, dispatchSetCurrentSegments]); // audioStateRef is a ref

    useEffect(() => {
        const currentVadRef = vadRef.current;
        const currentWsRef = wsRef.current;
        const currentVadLastSpeechEndedTimerRef = vadLastSpeechEndedTimerRef.current;

        return () => {
            console.log(`Effect (${purpose}): Microphone component unmounting. Cleaning up...`);
            currentVadRef?.destroy();
            if (currentWsRef && (currentWsRef.readyState === WebSocket.OPEN || currentWsRef.readyState === WebSocket.CONNECTING)) {
                currentWsRef.onopen = null;
                currentWsRef.onmessage = null;
                currentWsRef.onerror = null;
                currentWsRef.onclose = null;
                currentWsRef.close(1000, `Component unmounting (${purpose})`);
            }
            if (currentVadLastSpeechEndedTimerRef) { clearTimeout(currentVadLastSpeechEndedTimerRef); }
        };
    }, [purpose]);

    const isCurrentlyRecording = audioState === AudioState.RECORDING;
    const isCurrentlyPaused = audioState === AudioState.PAUSED;
    const isCurrentlyStopped = audioState === AudioState.STOPPED || audioState === AudioState.ERROR;
    const isCurrentlyProcessing = audioState === AudioState.PROCESSING;
    const isCurrentlyInitializing = audioState === AudioState.INITIALIZING || wsState === WebSocketState.CONNECTING;

    const canStartThisRecording = !globalIsMicrophoneDisabled && !!consultationId && !!consultationNoteId && !!securityPin;
    const canThisInteract = canStartThisRecording && !isCurrentlyInitializing && !isCurrentlyProcessing;

    const reduxIsLoading = useAppSelector(state => purpose === 'main' ? state.consultationNote.isLoadingTranscription : state.consultationNote.isLoadingAdditionalTranscription);

    let statusText = t('startRecordingInfo');
    if (!consultationId || !consultationNoteId || !securityPin) statusText = t('loadingDetails');
    else if (globalIsMicrophoneDisabled) statusText = t('micInUse');
    else if (isCurrentlyInitializing) statusText = t('connecting') + '...';
    else if (isCurrentlyProcessing || reduxIsLoading) statusText = t('transcribing') + '...';
    else if (isCurrentlyRecording) statusText = t('listening');
    else if (isCurrentlyPaused) statusText = t('recordingPaused');
    else if (error && audioState === AudioState.ERROR) statusText = error;
    else if (error) statusText = t('error');


    return (
        <>
            <Box sx={{
                display: 'flex', flexDirection: 'row', alignItems: 'center',
                justifyContent: 'space-between',
                borderRadius: 2,
                backgroundColor: colors?.container,
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
                paddingLeft: 2, paddingRight: 2,
                boxSizing: 'border-box',
                minHeight: '70px',
                flexShrink: 0,
                width: '100%',
                height: 'auto',
                position: 'relative',
                margin: purpose === 'main' && isMobile ? '0 0 8px 0' : '8px 0',
                top: 'auto',
                left: 'auto',
            }}>
                <Box
                    onClick={!canThisInteract ? undefined : (isCurrentlyRecording || isCurrentlyPaused ? handleStopRecording : handleStartRecordingRequest)}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 60,
                        height: 60,
                        flexShrink: 0,
                        borderRadius: '50%', borderStyle: 'solid', borderWidth: 1,
                        cursor: !canThisInteract ? 'not-allowed' : 'pointer',
                        opacity: !canThisInteract ? 0.5 : 1,
                        color: !canStartThisRecording ? 'grey' : (isCurrentlyRecording ? 'white' : colors?.main),
                        borderColor: !canStartThisRecording ? 'rgb(180, 180, 180)' : (isCurrentlyRecording ? colors?.red : colors?.main),
                        backgroundColor: !canStartThisRecording ? 'rgb(180, 180, 180)' : (isCurrentlyRecording ? colors?.red : 'transparent'),
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.2)',
                        '&:hover': !canThisInteract ? {} : { color: 'white', backgroundColor: isCurrentlyRecording ? 'rgb(232, 98, 30)' : colors?.main, borderColor: isCurrentlyRecording ? 'rgb(232, 98, 30)' : colors?.main, }
                    }}
                > <MicNoneIcon sx={{ fontSize: 40 }} /> </Box>

                <Box
                    sx={{
                        display: 'flex', alignItems: 'center',
                        color: !canStartThisRecording ? 'rgb(150, 150, 150)' : (isCurrentlyRecording ? colors?.red : (isCurrentlyProcessing || reduxIsLoading ? colors?.orange : (audioState === AudioState.ERROR ? colors?.red : 'rgb(150,150,150)'))),
                        flexGrow: 1, justifyContent: 'center', textAlign: 'center', paddingLeft: 2, paddingRight: 2, paddingBottom: 1, paddingTop: 1, fontSize: '0.9rem'
                    }}>
                    {statusText}
                    {audioState === AudioState.ERROR && !isCurrentlyInitializing && <span style={{ color: colors?.red, marginLeft: '8px', fontWeight: 'bold' }}>!</span>}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minWidth: '60px' }}>
                    {(isCurrentlyRecording || isCurrentlyPaused || isCurrentlyProcessing || (recordingTime > 0 && audioState !== AudioState.STOPPED)) && !isCurrentlyInitializing ? (
                        <Box sx={{ color: isCurrentlyRecording ? colors?.red : colors?.text, fontSize: 20 }}> {formatTime(recordingTime)} </Box>
                    ) : (
                        (canStartThisRecording && !isCurrentlyInitializing) ? (
                             <Box 
                                onClick={purpose === 'main' ? handleSettings : undefined} 
                                sx={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    padding: 1, 
                                    borderRadius: 3, 
                                    cursor: purpose === 'main' ? 'pointer' : 'default', 
                                    '&:hover': purpose === 'main' ? { backgroundColor: colors?.sectionHover } : {}
                                }}
                             >
                                 <MoreVertIcon sx={{ color: purpose === 'main' ? colors?.main : 'transparent' }} />
                             </Box>
                        ) : <Box sx={{ width: 40, height: 40 }} />
                    )}
                </Box>
            </Box>

            {purpose === 'main' && (
                <Box sx={{
                    display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center',
                    width: '100%', height: 100, borderRadius: 2,
                    bottom: (isRecorderBarOpened && !isRecorderBarDisabled) ? 0 : -110, zIndex: 10000, left: 0, position: 'fixed', backgroundColor: colors?.navBar,
                    color: colors?.main, borderTop: `2px solid ${colors?.main}`, boxShadow: '0 -4px 6px rgba(0, 0, 0, 0.2)', transition: 'bottom 0.25s ease-in-out',
                    boxSizing: 'border-box',
                }}>
                    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'space-evenly', fontSize: 30, width: '100%', maxWidth: '500px' }}>
                        <Box sx={{ minWidth: '80px', textAlign: 'center', color: isCurrentlyRecording ? colors?.red : colors?.text }}> {formatTime(recordingTime)} </Box>
                        <Box onClick={isCurrentlyRecording ? pauseRecording : resumeRecording} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: (isCurrentlyRecording || isCurrentlyPaused) ? 'pointer' : 'not-allowed', opacity: (isCurrentlyRecording || isCurrentlyPaused) ? 1 : 0.5, marginLeft: isMobile ? 2 : 5, marginRight: isMobile ? 2 : 5, padding: 1, borderRadius: '50%', '&:hover': (isCurrentlyRecording || isCurrentlyPaused) ? { backgroundColor: colors?.sectionHover, color: colors?.mainHover } : {} }}>
                            {isCurrentlyRecording ? <PauseOutlinedIcon sx={{ fontSize: 40 }} /> : <PlayArrowIcon sx={{ fontSize: 40 }} />}
                        </Box>
                        <Box onClick={(isCurrentlyRecording || isCurrentlyPaused) ? resetAudio : undefined} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: (isCurrentlyRecording || isCurrentlyPaused) ? 'pointer' : 'not-allowed', opacity: (isCurrentlyRecording || isCurrentlyPaused) ? 1 : 0.5, padding: 1, borderRadius: '50%', '&:hover': (isCurrentlyRecording || isCurrentlyPaused) ? { backgroundColor: colors?.sectionHover, color: colors?.mainHover } : {} }}>
                            <RestartAltOutlinedIcon sx={{ fontSize: 40 }} />
                        </Box>
                        <Button title={t('done')} triggerFunction={handleStopRecording} extraStyle={{ marginTop: 0, marginLeft: isMobile ? 2 : 4, minWidth: isMobile ? '80px' : '100px' }} isDisabled={isCurrentlyStopped || isCurrentlyInitializing || isCurrentlyProcessing} />
                    </Box>
                    <CancelButton handler={hideRecorderBar} />
                </Box>
            )}
        </>
    );
};

export default Microphone;